'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Spinner,
  Tabs,
  Tab,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  RangeCalendar,
} from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { CampaignsStatsTable, FiltersSection, CampaignStatsModal } from './components';
import { CampaignStats, CampaignsStatsFilters } from './types';
import { DEFAULT_PAGE_SIZE, getTableColumns, SPINNER_CLASSNAMES } from './constants';
import partnerAuthService from '../../../api/partner/partnerAuthService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import { CalendarFilterIc, ResetFiltersIc } from '../../../components/icons';
import { Tooltip } from '@heroui/react';

const Campaigns: React.FC = () => {
  const { t } = useTranslations();
  const breadcrumbs = useMemo(
    () => [
      { label: t('salesFans.home'), href: '/' },
      { label: t('sidebar.campaigns'), isActive: true },
    ],
    [t]
  );

  usePageBreadcrumbs(breadcrumbs);
  // State
  const [campaignType, setCampaignType] = useState<'cpl' | 'revshare'>('cpl');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [data, setData] = useState<CampaignStats[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<CampaignsStatsFilters>({
    // campaign_type: 'cpl'
  });

  // Date range state
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState<any>(null);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  // Visible columns state
  const allColumnsKeys = getTableColumns(t).map((col) => col.key);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(allColumnsKeys);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignStats | null>(null);

  // Date range formatting and handling
  const formatDateRange = (dateRange: any) => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return t('transactions.filters.date') || 'Дата';
    }

    const startDate = dateRange.start.toDate(getLocalTimeZone());
    const endDate = dateRange.end.toDate(getLocalTimeZone());

    return `${startDate.toLocaleDateString('ru-RU')} - ${endDate.toLocaleDateString('ru-RU')}`;
  };

  const handleDateRangeChange = (range: any) => {
    setSelectedDateRange(range);

    const formatDateToString = (dateValue: any) => {
      const localDate = dateValue.toDate(getLocalTimeZone());
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (range?.start && range?.end) {
      const startDateString = formatDateToString(range.start);
      const endDateString = formatDateToString(range.end);
      setStartDate(startDateString);
      setEndDate(endDateString);
      setIsCalendarOpen(false);
      setCurrentPage(1);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  // Sync calendar state with date filters
  useEffect(() => {
    if (startDate && endDate) {
      setSelectedDateRange({
        start: parseDate(startDate),
        end: parseDate(endDate),
      });
    } else {
      setSelectedDateRange(null);
    }
  }, [startDate, endDate]);

  // Fetch data
  const fetchCampaignsStats = useCallback(async () => {
    try {
      setIsLoading(true);

      const params = {
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
        search: search || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        ...filters,
      };

      const response =
        campaignType === 'cpl'
          ? await partnerAuthService.getCplCampaignsStats(params)
          : await partnerAuthService.getRefShareCampaignsStats(params);

      if (response && response.data) {
        setData(response.data.objects || []);
        setTotalCount(response.data.count || 0);
      } else {
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching campaigns stats:', error);
      setData([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, sortBy, search, filters, startDate, endDate, campaignType]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchCampaignsStats();
  }, [fetchCampaignsStats]);

  // Handlers
  const handleCampaignTypeChange = useCallback((key: string | number) => {
    const newType = key as 'cpl' | 'revshare';
    setCampaignType(newType);
    // setFilters(prev => ({ ...prev, campaign_type: newType }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((newSortBy: string) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  const handleVisibleColumnsChange = useCallback((columns: string[]) => {
    setVisibleColumns(columns);
  }, []);

  const handleRowClick = useCallback((campaign: CampaignStats) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedCampaign(null);
  }, []);

  const handleResetFilters = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setSelectedDateRange(null);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return startDate !== null || endDate !== null;
  }, [startDate, endDate]);

  return (
    <div className="relative flex flex-col h-full bg-white" aria-busy={isLoading}>
      {isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-sm">
          <Spinner size="lg" classNames={SPINNER_CLASSNAMES} />
        </div>
      )}
      {/* Header */}
      <div className="px-4 pt-6 md:px-6 md:py-[.6563rem]">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('campaignsStats.title') || 'Кампании'}
        </Typography>
      </div>

      {/* Content */}
      <div className="px-4 py-6 md:px-6 md:py-4 flex-1 space-y-6">
        {/* Date Range Picker */}
        <div className="flex items-center gap-4">
          <Popover
            isOpen={isCalendarOpen}
            onOpenChange={setIsCalendarOpen}
            placement="bottom-start"
          >
            <PopoverTrigger>
              <Button
                startContent={<CalendarFilterIc />}
                className={`bg-auth-surface border-none ${selectedDateRange ? '!text-auth-ink' : '!text-auth-muted'} rounded-2 !px-3 !min-h-9 !h-9 w-fit max-w-[20rem] justify-start`}
              >
                {formatDateRange(selectedDateRange)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <div
                className="w-auto partner-range-calendar"
                style={
                  {
                    '--heroui-primary': '#006FEE',
                    '--nextui-primary': '#006FEE',
                    '--heroui-focus': '#006FEE',
                    '--nextui-focus': '#006FEE',
                  } as React.CSSProperties
                }
              >
                <RangeCalendar
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  minValue={parseDate('2025-01-01')}
                  maxValue={today(getLocalTimeZone())}
                  className="w-auto"
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Campaign Type Tabs */}
        <div className="mb-4 ">
          <Typography variant="h6" className="font-medium text-gray-900 mb-4">
            {t('campaignsStats.sections.subscribersCampaigns') ||
              'Эффективность кампаний подписчиков'}
          </Typography>
          <div className="flex md:flex-row flex-col md:items-center gap-4">
            <div className="bg-gray-100 rounded-3 w-fit md:mb-4">
              <Tabs
                selectedKey={campaignType}
                onSelectionChange={handleCampaignTypeChange}
                variant="light"
                classNames={{
                  tabList: 'gap-1',
                  cursor: 'bg-white rounded-lg shadow-sm',
                  tab: 'px-3 py-1 text-sm font-normal data-[selected=true]:text-black text-gray-500',
                  tabContent: 'group-data-[selected=true]:text-black',
                }}
              >
                <Tab key="cpl" title="CPL" />
                <Tab key="revshare" title="RevShare" />
              </Tabs>
            </div>

            <div className="flex items-center gap-4 max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
              {/* Filters */}
              <FiltersSection
                search={search}
                onSearchChange={handleSearchChange}
                visibleColumns={visibleColumns}
                onVisibleColumnsChange={handleVisibleColumnsChange}
                allColumns={getTableColumns(t).map((col) => ({ key: col.key, label: col.name }))}
              />

              {/* Reset Filters Button */}
              {hasActiveFilters && (
                <ResponsiveTooltip
                  content={t('transactions.resetFilters') || 'Сбросить фильтры'}
                  placement="top"
                >
                  <Button
                    //@ts-ignore
                    size="sm"
                    className="self-center !h-9 mb-4 !min-h-9 rounded-2 !min-w-9 !w-9 p-0 bg-auth-surface flex items-center justify-center shrink-0"
                    onPress={handleResetFilters}
                  >
                    <ResetFiltersIc className="w-5 h-5" />
                  </Button>
                </ResponsiveTooltip>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="h-full">
          <CampaignsStatsTable
            data={data}
            isLoading={isLoading}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            sortBy={sortBy}
            onSort={handleSort}
            visibleColumns={visibleColumns}
            onRowAction={handleRowClick}
          />
        </div>
      </div>

      {/* Campaign Stats Modal */}
      <CampaignStatsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        campaign={selectedCampaign}
      />
    </div>
  );
};

export default Campaigns;
